import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@artic/ui/breadcrumb';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Link, useRouterState, type AnyRouteMatch } from '@tanstack/react-router';
import { Fragment, useEffect, useReducer, type ReactNode } from 'react';

interface Crumb {
  /** Label rendered in the breadcrumb bar and joined into document.title. */
  label: string;
  /** Pathname for the breadcrumb <Link>. */
  path: string;
}

/**
 * Subscribe to the React Query cache and force a re-render on any cache
 * event (data fetched, updated, invalidated). Breadcrumbs whose labels
 * come from cached data use this to re-resolve their label as soon as the
 * underlying query resolves.
 */
function useRerenderOnQueryCache(queryClient: QueryClient): void {
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    // Defer via microtask: cache events fire synchronously when a sibling
    // component's `useQuery` mounts its observer mid-render. Updating
    // RouteBreadcrumbs in that window trips React's "Cannot update a
    // component while rendering a different component" warning.
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      queueMicrotask(forceRender);
    });
    return () => {
      unsubscribe();
    };
  }, [queryClient]);
}

/**
 * Walks `state.matches` to extract:
 * - the most-specific `section` (leaf-first, first-found wins, mirroring
 *   the `useLayoutMode` pattern in `__root.tsx`);
 * - breadcrumb entries in root→leaf order, respecting a leaf's
 *   `clearParentBreadcrumbs` flag which drops all ancestor-contributed
 *   crumbs.
 *
 * Dynamic breadcrumb resolvers that return `null` are omitted entirely
 * (we show nothing until the data is ready — no "Loading…" placeholder).
 */
function resolveBreadcrumbs(
  matches: readonly AnyRouteMatch[],
  queryClient: QueryClient,
): { section: string | null; crumbs: Crumb[]; badges: ReactNode } {
  // Section — leaf-first, most-specific wins.
  let section: string | null = null;
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    if (!match) continue;
    const candidate = match.staticData.section;
    if (typeof candidate === 'string') {
      section = candidate;
      break;
    }
  }

  // Determine where to start collecting breadcrumbs from. If any match
  // declares `clearParentBreadcrumbs`, we start at the first such match so
  // every ancestor crumb is dropped.
  let startIndex = 0;
  for (let i = matches.length - 1; i >= 0; i--) {
    if (matches[i]?.staticData.clearParentBreadcrumbs) {
      startIndex = i;
      break;
    }
  }

  const crumbs: Crumb[] = [];
  for (let i = startIndex; i < matches.length; i++) {
    const match = matches[i];
    if (!match) continue;
    const raw = match.staticData.breadcrumb;
    if (typeof raw === 'string') {
      crumbs.push({ label: raw, path: match.pathname });
    } else if (typeof raw === 'function') {
      const resolved = raw(match, queryClient);
      if (typeof resolved === 'string') {
        crumbs.push({ label: resolved, path: match.pathname });
      } else if (Array.isArray(resolved)) {
        // A resolver may contribute several crumbs from one route (e.g. the
        // quality check detail emits "{work order} > {check}" from a single
        // un-nested route), each with its own link target.
        crumbs.push(...resolved);
      }
      // null → omit the entry until the underlying data resolves.
    }
  }

  // Badge adornments — leaf-first, most-specific wins. Rendered after the
  // final crumb; never part of document.title. Honours the same
  // clearParentBreadcrumbs start index as the crumbs so a dropped
  // ancestor can't contribute badges either.
  let badges: ReactNode = null;
  for (let i = matches.length - 1; i >= startIndex; i--) {
    const match = matches[i];
    if (!match) continue;
    const resolver = match.staticData.breadcrumbBadges;
    if (typeof resolver === 'function') {
      badges = resolver(match, queryClient);
      break;
    }
  }

  return { section, crumbs, badges };
}

/**
 * Renders the breadcrumb trail from the current route matches and keeps
 * `document.title` in sync with the resolved breadcrumbs.
 *
 * Each route declares its contribution via `staticData`:
 *   - `section`: first (non-navigable) label — e.g. "Production Planning".
 *   - `breadcrumb`: string label for lists, or a `(match, qc) => string | null`
 *     resolver for detail routes that need the actual entity name.
 *   - `clearParentBreadcrumbs`: discard ancestor crumbs (sidebar override).
 *
 * See `router-types.d.ts` for the full contract.
 */
export function RouteBreadcrumbs() {
  const queryClient = useQueryClient();
  // Re-render on every query-cache mutation so dynamic resolvers can
  // pick up freshly-fetched data.
  useRerenderOnQueryCache(queryClient);

  const matches = useRouterState({
    select: (state) => state.matches,
  });

  const { section, crumbs, badges } = resolveBreadcrumbs(matches, queryClient);

  // ─── Tab title ─────────────────────────────────────────────────
  // Always lead with "Artic", then append the section and every resolved
  // crumb label. Re-runs on every render; no cleanup (the component is
  // mounted at the root and never unmounts during SPA navigation, and a
  // cleanup would briefly flash "Artic" between routes).
  const titleParts = ['Artic'];
  if (section) titleParts.push(section);
  for (const crumb of crumbs) titleParts.push(crumb.label);
  const title = titleParts.join(' | ');
  useEffect(() => {
    document.title = title;
  }, [title]);

  if (!section && crumbs.length === 0) return null;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {section && (
          <>
            <BreadcrumbItem className="hidden md:block">
              <span className="text-muted-foreground">{section}</span>
            </BreadcrumbItem>
            {crumbs.length > 0 && <BreadcrumbSeparator className="hidden md:block" />}
          </>
        )}
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          return (
            // A layout route and its index can resolve to the same pathname
            // (e.g. `/factories` + its index crumb), so the index disambiguates.
            <Fragment key={`${index}:${crumb.path}`}>
              {index > 0 && <BreadcrumbSeparator className="hidden md:block" />}
              <BreadcrumbItem className={!isLast ? 'hidden md:block' : ''}>
                {isLast ? (
                  <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.path}>{crumb.label}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </Fragment>
          );
        })}
        {/* ReactNode includes booleans — exclude `false` so a resolver
            using `cond && <Badge/>` can't render an empty item. */}
        {badges != null && badges !== false && (
          <BreadcrumbItem className="gap-1.5">{badges}</BreadcrumbItem>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
